import { combineClass, type JSX } from 'cdui-js';
import {
  Show,
  createEffect,
  createMemo,
  createSignal,
  mergeProps,
  onCleanup,
} from 'solid-js';
import { Portal } from 'solid-js/web';
import './difi-preview.css';

const MIN_SCALE = 0.5;
const MAX_SCALE = 5;
const WHEEL_STEP = 0.12;

export type DiFiPreviewProps = {
  /** 是否展示 */
  visible?: boolean;
  /** 单图地址；与 `urls` 二选一 */
  src?: string;
  /** 多图列表；有值时优先于 `src` */
  urls?: string[];
  /** 多图时初始下标，默认 0 */
  startIndex?: number;
  /** 关闭（点遮罩、关闭钮等） */
  onClose?: () => void;
  /** 层级，默认 10000（高于 Toast/弹窗，避免关闭钮被挡） */
  zIndex?: number;
  class?: string;
  style?: JSX.CSSProperties;
};

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

export const DiFiPreview = (rawProps: DiFiPreviewProps) => {
  const props = mergeProps(
    {
      visible: false,
      startIndex: 0,
      zIndex: 10000,
    },
    rawProps,
  );

  const list = createMemo(() => {
    const u = props.urls;
    if (u && u.length > 0) return u.filter(Boolean);
    if (props.src && String(props.src).trim()) return [String(props.src).trim()];
    return [];
  });

  const [index, setIndex] = createSignal(0);
  const [scale, setScale] = createSignal(1);
  const [tx, setTx] = createSignal(0);
  const [ty, setTy] = createSignal(0);
  /** 非手势缩放/拖拽时加 transition */
  const [transformAnimated, setTransformAnimated] = createSignal(true);

  let pinchStartDist = 0;
  let pinchStartScale = 1;
  let dragStartX = 0;
  let dragStartY = 0;
  let dragOriginTx = 0;
  let dragOriginTy = 0;
  let touchMode: 'none' | 'pinch' | 'pan' = 'none';
  /** 本次手势是否发生过拖拽/缩放，避免松手误触关闭 */
  let gestureMoved = false;

  const resetView = (animated = true) => {
    setTransformAnimated(animated);
    setScale(1);
    setTx(0);
    setTy(0);
  };

  createEffect(() => {
    if (!props.visible) return;
    const len = list().length;
    const start = clamp(Math.floor(props.startIndex ?? 0), 0, Math.max(0, len - 1));
    setIndex(start);
    resetView(true);
  });

  createEffect(() => {
    if (!props.visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    onCleanup(() => {
      document.body.style.overflow = prev;
    });
  });

  const currentUrl = createMemo(() => {
    const arr = list();
    if (!arr.length) return '';
    return arr[clamp(index(), 0, arr.length - 1)] ?? '';
  });

  /** 延迟卸载，避免关闭后同一点击落到下层（缩略图）又打开预览 */
  const close = (e?: Event) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    window.setTimeout(() => {
      props.onClose?.();
    }, 50);
  };

  const zoomBy = (delta: number) => {
    setTransformAnimated(true);
    setScale((s) => clamp(s + delta, MIN_SCALE, MAX_SCALE));
  };

  const prev = () => {
    const arr = list();
    if (arr.length < 2) return;
    setTransformAnimated(true);
    setIndex((i) => (i - 1 + arr.length) % arr.length);
    resetView(true);
  };

  const next = () => {
    const arr = list();
    if (arr.length < 2) return;
    setTransformAnimated(true);
    setIndex((i) => (i + 1) % arr.length);
    resetView(true);
  };

  const onWheel = (e: WheelEvent) => {
    if (!props.visible) return;
    e.preventDefault();
    const dir = e.deltaY > 0 ? -WHEEL_STEP : WHEEL_STEP;
    zoomBy(dir);
  };

  const touchDist = (t: TouchList) => {
    if (t.length < 2) return 0;
    const dx = t[0].clientX - t[1].clientX;
    const dy = t[0].clientY - t[1].clientY;
    return Math.hypot(dx, dy);
  };

  const onTouchStart = (e: TouchEvent) => {
    gestureMoved = false;
    if (e.touches.length === 2) {
      touchMode = 'pinch';
      setTransformAnimated(false);
      pinchStartDist = touchDist(e.touches);
      pinchStartScale = scale();
    } else if (e.touches.length === 1 && scale() > 1) {
      touchMode = 'pan';
      setTransformAnimated(false);
      dragStartX = e.touches[0].clientX;
      dragStartY = e.touches[0].clientY;
      dragOriginTx = tx();
      dragOriginTy = ty();
    } else {
      touchMode = 'none';
    }
  };

  const onTouchMove = (e: TouchEvent) => {
    if (touchMode === 'pinch' && e.touches.length >= 2) {
      e.preventDefault();
      gestureMoved = true;
      const d = touchDist(e.touches);
      if (pinchStartDist > 0) {
        const nextScale = clamp(pinchStartScale * (d / pinchStartDist), MIN_SCALE, MAX_SCALE);
        setScale(nextScale);
      }
    } else if (touchMode === 'pan' && e.touches.length === 1) {
      e.preventDefault();
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      if (Math.hypot(x - dragStartX, y - dragStartY) > 6) gestureMoved = true;
      setTx(dragOriginTx + (x - dragStartX));
      setTy(dragOriginTy + (y - dragStartY));
    }
  };

  const onTouchEnd = () => {
    touchMode = 'none';
    setTransformAnimated(true);
  };

  /** 单击图片关闭；缩放拖拽后松手不关 */
  const onImageAreaClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (gestureMoved) {
      gestureMoved = false;
      return;
    }
    close(e);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowLeft') prev();
    if (e.key === 'ArrowRight') next();
  };

  createEffect(() => {
    if (!props.visible) return;
    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => document.removeEventListener('keydown', onKeyDown));
  });

  return (
    <Portal
      mount={
        typeof document !== 'undefined'
          ? (document.getElementById('h5-shell') || document.body)
          : undefined!
      }
    >
      <Show when={props.visible && list().length > 0}>
        <div
          class={combineClass('difi-preview', props.class ?? '')}
          style={{
            'z-index': props.zIndex,
            ...props.style,
          }}
          role="dialog"
          aria-modal="true"
          aria-label="图片预览"
          onwheel={onWheel}
        >
          <button
            type="button"
            class="difi-preview__close"
            onclick={(e) => close(e)}
            aria-label="关闭"
          >
            ×
          </button>
          <div class="difi-preview__mask" onclick={(e) => close(e)} aria-hidden="true" />

          <Show when={list().length > 1}>
            <div class="difi-preview__pager">
              <button type="button" class="difi-preview__btn difi-preview__btn--text" onclick={prev}>
                上一张
              </button>
              <span>
                {index() + 1} / {list().length}
              </span>
              <button type="button" class="difi-preview__btn difi-preview__btn--text" onclick={next}>
                下一张
              </button>
            </div>
          </Show>

          <div class="difi-preview__stage">
            <Show when={list().length > 1}>
              <button
                type="button"
                class="difi-preview__side-nav difi-preview__side-nav--prev"
                onclick={(e) => {
                  e.stopPropagation();
                  prev();
                }}
                aria-label="上一张"
              >
                ‹
              </button>
              <button
                type="button"
                class="difi-preview__side-nav difi-preview__side-nav--next"
                onclick={(e) => {
                  e.stopPropagation();
                  next();
                }}
                aria-label="下一张"
              >
                ›
              </button>
            </Show>
            <div
              class={combineClass(
                'difi-preview__transform',
                transformAnimated() && 'difi-preview__transform--animated',
              )}
              style={{
                transform: `translate3d(${tx()}px, ${ty()}px, 0) scale(${scale()})`,
              }}
              ontouchstart={onTouchStart}
              ontouchmove={onTouchMove}
              ontouchend={onTouchEnd}
              ontouchcancel={onTouchEnd}
              onclick={onImageAreaClick}
            >
              <img class="difi-preview__img" src={currentUrl()} alt="" draggable={false} />
            </div>
          </div>
        </div>
      </Show>
    </Portal>
  );
};
